import React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal';
import { useSignOutStore } from '../../store/signOutStore';
import { useTeamsStore } from '../../store/teamsStore';

/**
 * Asked before an explicit sign-out would delete something that exists only
 * on this device. The flow and the reasons live in `store/signOutStore.js`.
 */
const SECONDARY =
  'px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm disabled:opacity-50';
const PRIMARY =
  'px-3 py-1.5 rounded-lg transition-colors modern-button text-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50';
const DANGER =
  'px-3 py-1.5 rounded-lg transition-colors modern-button text-sm bg-red-600 hover:bg-red-700 text-white disabled:opacity-50';

export default function SignOutLocalDataDialog() {
  const { t } = useTranslation();
  const prompt = useSignOutStore((s) => s.prompt);
  const busy = useSignOutStore((s) => s.busy);
  const confirm = useSignOutStore((s) => s.confirm);
  const cancel = useSignOutStore((s) => s.cancel);
  const teams = useTeamsStore((s) => s.teams);

  if (!prompt) return null;
  const s = prompt.summary;
  const lines = s
    ? [
        s.deviceBoards > 0 && t('auth.signOutDialog.boards', { count: s.deviceBoards }),
        s.deviceDocuments > 0 && t('auth.signOutDialog.documents', { count: s.deviceDocuments }),
        s.deviceOther > 0 && t('auth.signOutDialog.other', { count: s.deviceOther }),
        s.unsyncedChanges > 0 && t('auth.signOutDialog.unsynced', { count: s.unsyncedChanges }),
      ].filter(Boolean)
    : [];
  // Workspaces tracked with git on this device only: every saved version is in
  // this account's folder and nowhere else (04.10.2026). Known even when the
  // database summary is not.
  const histories = Array.isArray(prompt.localHistoryIds) ? prompt.localHistoryIds.length : 0;
  if (histories > 0) lines.push(t('auth.signOutDialog.histories', { count: histories }));
  // "My projects, boards & documents" tracked on this device only (T10): one more such history.
  const scopeHistories = Array.isArray(prompt.localScopeFolders) ? prompt.localScopeFolders.length : 0;
  if (scopeHistories > 0) lines.push(t('auth.signOutDialog.scopeHistory'));
  const allHistories = histories + scopeHistories;
  // Cloud scopes "Keep" keeps WHOLE because they hold a queued change —
  // requests (with their headers) and environments included. Named, because
  // "everything in the cloud is removed" would otherwise be untrue for them.
  const kept = Array.isArray(s?.keptScopes) ? s.keptScopes : [];
  const teamName = (k) => {
    const id = String(k.scopeId || '').replace(/^team:/, '');
    const team = (Array.isArray(teams) ? teams : []).find((tm) => String(tm?.id) === id);
    return team?.name || t('auth.signOutDialog.unnamedTeam');
  };
  const scopeName = (k) => {
    if (k.kind === 'personal') return t('auth.signOutDialog.personalSpace');
    if (k.kind === 'team') return teamName(k);
    return k.name || t('auth.signOutDialog.unnamedWorkspace');
  };

  return (
    <Modal
      isOpen
      onClose={cancel}
      title={t('auth.signOutDialog.title')}
      type="warning"
      preventBackdropClose={busy}
      preventEscapeClose={busy}
    >
      <div className="space-y-3 text-sm text-gray-600 dark:text-zinc-300" data-testid="sign-out-local-data">
        <p>{s ? t('auth.signOutDialog.intro') : t('auth.signOutDialog.unknown')}</p>
        {lines.length > 0 && (
          <ul className="list-disc space-y-1 pl-5">
            {lines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        )}
        {s?.refusedChanges > 0 && (
          <p>{t('auth.signOutDialog.refused', { count: s.refusedChanges })}</p>
        )}
        <p>{t('auth.signOutDialog.keepExplained')}</p>
        {kept.length > 0 && (
          <div data-testid="sign-out-kept-scopes">
            <p>{t('auth.signOutDialog.keptWholeIntro')}</p>
            <ul className="list-disc space-y-1 pl-5">
              {kept.map((k) => (
                <li key={k.scopeId}>
                  {t('auth.signOutDialog.keptWholeItem', {
                    name: scopeName(k),
                    requests: t('auth.signOutDialog.keptRequests', { count: k.requests || 0 }),
                    environments: t('auth.signOutDialog.keptEnvironments', { count: k.environments || 0 }),
                    records: t('auth.signOutDialog.keptRecords', { count: k.records || 0 }),
                  })}
                </li>
              ))}
            </ul>
          </div>
        )}
        <p>{t('auth.signOutDialog.removeExplained')}</p>
        {allHistories > 0 && (
          <p data-testid="sign-out-histories-removed">
            {scopeHistories > 0
              ? t('auth.signOutDialog.historiesRemovedAll', { count: allHistories })
              : t('auth.signOutDialog.historiesRemoved', { count: histories })}
          </p>
        )}
        <div className="flex flex-wrap justify-end gap-2 pt-2">
          <button type="button" className={SECONDARY} onClick={cancel} disabled={busy}>
            {t('common.cancel')}
          </button>
          <button type="button" className={DANGER} onClick={() => confirm('remove')} disabled={busy}>
            {t('auth.signOutDialog.remove')}
          </button>
          <button type="button" className={PRIMARY} onClick={() => confirm('keep')} disabled={busy} autoFocus>
            {t('auth.signOutDialog.keep')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
